import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Building2, Users, Tag, BookOpen, Plus, Edit2, Trash2, Lock, Unlock, DollarSign, Calendar, Globe } from 'lucide-react';

const Settings = () => {
  const { company, user, refreshCompany, isAdmin, canEdit } = useAuth();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [counterparties, setCounterparties] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [openingBalanceAccounts, setOpeningBalanceAccounts] = useState([]);
  const [openingBalances, setOpeningBalances] = useState({});
  const [hasOpeningBalance, setHasOpeningBalance] = useState(false);
  const [openingBalanceDate, setOpeningBalanceDate] = useState(new Date().toISOString().split('T')[0]);

  // Dialog states
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [counterpartyDialogOpen, setCounterpartyDialogOpen] = useState(false);
  const [closePeriodDialog, setClosePeriodDialog] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState(null);

  // Form states
  const [companyForm, setCompanyForm] = useState({
    name: company?.name || '',
    email: company?.email || '',
    phone: company?.phone || '',
    address: company?.address || '',
    tax_id: company?.tax_id || '',
    currency: company?.currency || 'USD'
  });

  const [newCategory, setNewCategory] = useState({
    name: '',
    category_type: 'expense',
    icon: 'circle',
    color: '#6B7280'
  });

  const [newCounterparty, setNewCounterparty] = useState({
    name: '',
    counterparty_type: 'customer',
    email: '',
    phone: '',
    address: ''
  });

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      const [catRes, accRes, cpRes, periodRes, obRes, currRes] = await Promise.all([
        api.get('/categories?is_active=true'),
        api.get('/accounts?is_active=true'),
        api.get('/counterparties'),
        api.get('/periods/status'),
        api.get('/periods/opening-balances'),
        api.get('/currency/supported')
      ]);
      setCategories(catRes.data);
      setAccounts(accRes.data);
      setCounterparties(cpRes.data);
      setPeriods(periodRes.data.periods || []);
      setOpeningBalanceAccounts(obRes.data.accounts || []);
      setOpeningBalances(obRes.data.existing_balances || {});
      setHasOpeningBalance(obRes.data.has_opening_balance);
      setCurrencies(currRes.data || []);
      if (obRes.data.opening_balance_date) {
        setOpeningBalanceDate(obRes.data.opening_balance_date);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    }
  };

  const handleCompanyUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.put('/companies/current', companyForm);
      // Refresh company data in auth context to update currency everywhere
      await refreshCompany();
      toast.success('Company settings updated');
    } catch (error) {
      toast.error('Failed to update company');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    try {
      // Get a mapped account for the category type
      const mappedAccount = accounts.find(a => {
        if (newCategory.category_type === 'revenue') return a.account_type === 'revenue';
        if (newCategory.category_type === 'expense') return a.account_type === 'expense';
        return false;
      });

      if (!mappedAccount) {
        toast.error('No account found for this category type');
        return;
      }

      await api.post('/categories', {
        ...newCategory,
        mapped_account_id: mappedAccount.id
      });
      toast.success('Category created');
      setCategoryDialogOpen(false);
      setNewCategory({ name: '', category_type: 'expense', icon: 'circle', color: '#6B7280' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create category');
    }
  };

  const handleCreateCounterparty = async (e) => {
    e.preventDefault();
    try {
      await api.post('/counterparties', newCounterparty);
      toast.success('Contact created');
      setCounterpartyDialogOpen(false);
      setNewCounterparty({ name: '', counterparty_type: 'customer', email: '', phone: '', address: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create contact');
    }
  };

  const handleDeleteCounterparty = async (id) => {
    if (!window.confirm('Are you sure you want to deactivate this contact?')) return;
    try {
      await api.delete(`/counterparties/${id}`);
      toast.success('Contact deactivated');
      loadData();
    } catch (error) {
      toast.error('Failed to deactivate contact');
    }
  };

  const handleClosePeriod = async () => {
    if (!selectedPeriod) return;
    try {
      await api.post('/periods/close', { period: selectedPeriod.period });
      toast.success(`Period ${selectedPeriod.period} has been closed`);
      setClosePeriodDialog(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to close period');
    }
  };

  const handleReopenPeriod = async (period) => {
    if (!window.confirm(`Are you sure you want to reopen period ${period}? This will allow transactions to be modified.`)) return;
    try {
      await api.post('/periods/reopen', { period });
      toast.success(`Period ${period} has been reopened`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reopen period');
    }
  };

  const handleSaveOpeningBalances = async () => {
    try {
      const entries = Object.entries(openingBalances)
        .filter(([_, balance]) => balance !== 0 && balance !== '' && balance !== null)
        .map(([account_id, balance]) => ({
          account_id,
          balance: parseFloat(balance) || 0
        }));

      if (entries.length === 0) {
        toast.error('Please enter at least one balance');
        return;
      }

      await api.post('/periods/opening-balances', {
        as_of_date: openingBalanceDate,
        entries
      });
      toast.success('Opening balances saved successfully');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save opening balances');
    }
  };

  const updateOpeningBalance = (accountId, value) => {
    setOpeningBalances(prev => ({
      ...prev,
      [accountId]: value === '' ? '' : parseFloat(value) || 0
    }));
  };

  // Calculate totals for opening balance validation
  const calculateTotals = () => {
    let assets = 0, liabilities = 0, equity = 0;
    openingBalanceAccounts.forEach(account => {
      const balance = parseFloat(openingBalances[account.id]) || 0;
      if (account.account_type === 'asset') assets += balance;
      else if (account.account_type === 'liability') liabilities += balance;
      else if (account.account_type === 'equity') equity += balance;
    });
    return { assets, liabilities, equity, difference: assets - (liabilities + equity) };
  };

  const totals = calculateTotals();

  return (
    <div className="animate-fade-in">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>
          Settings
        </h1>
        <p className="text-[hsl(var(--muted-ink))] mt-1">
          Manage your company and preferences
        </p>
      </div>

      <Tabs defaultValue="company">
        <TabsList className="mb-6 flex-wrap">
          <TabsTrigger value="company" className="gap-2">
            <Building2 size={16} />
            Company
          </TabsTrigger>
          <TabsTrigger value="categories" className="gap-2">
            <Tag size={16} />
            Categories
          </TabsTrigger>
          <TabsTrigger value="contacts" className="gap-2">
            <Users size={16} />
            Contacts
          </TabsTrigger>
          <TabsTrigger value="accounts" className="gap-2">
            <BookOpen size={16} />
            Accounts
          </TabsTrigger>
          <TabsTrigger value="opening" className="gap-2">
            <DollarSign size={16} />
            Opening Balances
          </TabsTrigger>
          <TabsTrigger value="periods" className="gap-2">
            <Calendar size={16} />
            Periods
          </TabsTrigger>
        </TabsList>

        {/* Company Settings */}
        <TabsContent value="company">
          <Card>
            <CardHeader>
              <CardTitle>Company Information</CardTitle>
              <CardDescription>Update your company details</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCompanyUpdate} className="space-y-4 max-w-lg">
                <div>
                  <Label htmlFor="company_name">Company Name</Label>
                  <Input
                    id="company_name"
                    value={companyForm.name}
                    onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="company_email">Email</Label>
                  <Input
                    id="company_email"
                    type="email"
                    value={companyForm.email}
                    onChange={(e) => setCompanyForm({ ...companyForm, email: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="company_phone">Phone</Label>
                  <Input
                    id="company_phone"
                    value={companyForm.phone}
                    onChange={(e) => setCompanyForm({ ...companyForm, phone: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="company_address">Address</Label>
                  <Input
                    id="company_address"
                    value={companyForm.address}
                    onChange={(e) => setCompanyForm({ ...companyForm, address: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="company_tax_id">Tax ID</Label>
                  <Input
                    id="company_tax_id"
                    value={companyForm.tax_id}
                    onChange={(e) => setCompanyForm({ ...companyForm, tax_id: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="company_currency" className="flex items-center gap-2">
                    <Globe size={16} />
                    Default Currency
                  </Label>
                  <Select
                    value={companyForm.currency}
                    onValueChange={(value) => setCompanyForm({ ...companyForm, currency: value })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select currency" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {currencies.map((c) => (
                        <SelectItem key={c.code} value={c.code}>
                          {c.symbol} {c.code} - {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-[hsl(var(--muted-ink))] mt-1">
                    This will be the default currency for new transactions
                  </p>
                </div>
                <Button
                  type="submit"
                  className="bg-[hsl(var(--brand-500))] hover:bg-[hsl(var(--brand-600))] text-white"
                  disabled={loading || !isAdmin}
                  title={!isAdmin ? "Only admins can update company settings" : ""}
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
                {!isAdmin && (
                  <p className="text-sm text-amber-600 mt-2">Only admins can update company settings</p>
                )}
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Categories */}
        <TabsContent value="categories">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Categories</CardTitle>
                <CardDescription>Organize your transactions by category</CardDescription>
              </div>
              {canEdit && (
                <Button
                  onClick={() => setCategoryDialogOpen(true)}
                  className="bg-[hsl(var(--brand-500))] hover:bg-[hsl(var(--brand-600))] text-white"
                >
                  <Plus size={16} className="mr-2" />
                  Add Category
                </Button>
              )}
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Revenue Categories */}
                <div>
                  <h3 className="font-medium text-green-600 mb-3">Revenue Categories</h3>
                  <div className="space-y-2">
                    {categories.filter(c => c.category_type === 'revenue').map((cat) => (
                      <div key={cat.id} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: cat.color }} />
                          <span>{cat.name}</span>
                          {cat.is_system && <span className="text-xs text-gray-500">(System)</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Expense Categories */}
                <div>
                  <h3 className="font-medium text-red-600 mb-3">Expense Categories</h3>
                  <div className="space-y-2">
                    {categories.filter(c => c.category_type === 'expense').map((cat) => (
                      <div key={cat.id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: cat.color }} />
                          <span>{cat.name}</span>
                          {cat.is_system && <span className="text-xs text-gray-500">(System)</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Contacts */}
        <TabsContent value="contacts">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Contacts</CardTitle>
                <CardDescription>Manage customers, suppliers, and lenders</CardDescription>
              </div>
              {canEdit && (
                <Button
                  onClick={() => setCounterpartyDialogOpen(true)}
                  className="bg-[hsl(var(--brand-500))] hover:bg-[hsl(var(--brand-600))] text-white"
                >
                  <Plus size={16} className="mr-2" />
                  Add Contact
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {counterparties.length === 0 ? (
                <div className="text-center py-8 text-[hsl(var(--muted-ink))]">
                  No contacts yet. Add your first customer, supplier, or lender.
                </div>
              ) : (
                <div className="space-y-3">
                  {counterparties.map((cp) => (
                    <div key={cp.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{cp.name}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            cp.counterparty_type === 'customer' ? 'bg-green-100 text-green-700' :
                            cp.counterparty_type === 'supplier' ? 'bg-blue-100 text-blue-700' :
                            cp.counterparty_type === 'lender' ? 'bg-purple-100 text-purple-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {cp.counterparty_type}
                          </span>
                        </div>
                        {(cp.email || cp.phone) && (
                          <p className="text-sm text-[hsl(var(--muted-ink))]">
                            {cp.email}{cp.email && cp.phone ? ' • ' : ''}{cp.phone}
                          </p>
                        )}
                      </div>
                      {canEdit && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDeleteCounterparty(cp.id)}
                        >
                          <Trash2 size={16} />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Accounts (Chart of Accounts) */}
        <TabsContent value="accounts">
          <Card>
            <CardHeader>
              <CardTitle>Chart of Accounts</CardTitle>
              <CardDescription>Your accounting structure</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {['asset', 'liability', 'equity', 'revenue', 'expense'].map((type) => {
                  const typeAccounts = accounts.filter(a => a.account_type === type);
                  if (typeAccounts.length === 0) return null;
                  
                  return (
                    <div key={type}>
                      <h3 className={`font-medium mb-3 capitalize ${
                        type === 'asset' ? 'text-blue-600' :
                        type === 'liability' ? 'text-amber-600' :
                        type === 'equity' ? 'text-purple-600' :
                        type === 'revenue' ? 'text-green-600' :
                        'text-red-600'
                      }`}>
                        {type.charAt(0).toUpperCase() + type.slice(1)}s
                      </h3>
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-2 w-20">Code</th>
                            <th className="text-left py-2">Name</th>
                            <th className="text-left py-2">Subtype</th>
                            <th className="text-right py-2">Balance</th>
                          </tr>
                        </thead>
                        <tbody>
                          {typeAccounts.map((acc) => (
                            <tr key={acc.id} className="border-b">
                              <td className="py-2 text-[hsl(var(--muted-ink))]">{acc.code}</td>
                              <td className="py-2">
                                {acc.name}
                                {acc.is_system_account && (
                                  <span className="text-xs text-gray-400 ml-2">(System)</span>
                                )}
                              </td>
                              <td className="py-2 text-[hsl(var(--muted-ink))]">{acc.account_subtype}</td>
                              <td className="py-2 text-right numbered">
                                ${parseFloat(acc.current_balance || 0).toFixed(2)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Opening Balances Tab */}
        <TabsContent value="opening">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign size={20} />
                Opening Balances
              </CardTitle>
              <CardDescription>
                Set your starting balances for balance sheet accounts. Assets must equal Liabilities + Equity.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {hasOpeningBalance && (
                <div className="mb-4 p-3 bg-[hsl(var(--brand-50))] rounded-lg">
                  <p className="text-sm text-[hsl(var(--brand-700))]">
                    Opening balances have been set as of {openingBalanceDate}. You can update them below.
                  </p>
                </div>
              )}
              
              <div className="mb-4">
                <Label>Opening Balance Date</Label>
                <Input
                  type="date"
                  value={openingBalanceDate}
                  onChange={(e) => setOpeningBalanceDate(e.target.value)}
                  className="mt-1 w-48"
                />
              </div>

              {/* Validation Summary */}
              <div className={`p-4 rounded-lg mb-6 ${Math.abs(totals.difference) < 0.01 ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                <div className="grid grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Total Assets</p>
                    <p className="text-lg font-bold">${totals.assets.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Total Liabilities</p>
                    <p className="text-lg font-bold">${totals.liabilities.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Total Equity</p>
                    <p className="text-lg font-bold">${totals.equity.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Difference</p>
                    <p className={`text-lg font-bold ${Math.abs(totals.difference) < 0.01 ? 'text-green-600' : 'text-red-600'}`}>
                      ${totals.difference.toFixed(2)}
                      {Math.abs(totals.difference) < 0.01 && ' ✓'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Account Entries by Type */}
              {['asset', 'liability', 'equity'].map(type => {
                const typeAccounts = openingBalanceAccounts.filter(a => a.account_type === type);
                if (typeAccounts.length === 0) return null;
                return (
                  <div key={type} className="mb-6">
                    <h4 className="font-medium text-lg capitalize mb-3">{type}s</h4>
                    <div className="space-y-2">
                      {typeAccounts.map(account => (
                        <div key={account.id} className="flex items-center gap-4">
                          <span className="w-16 text-sm text-gray-500">{account.code}</span>
                          <span className="flex-1">{account.name}</span>
                          <Input
                            type="number"
                            step="0.01"
                            value={openingBalances[account.id] || ''}
                            onChange={(e) => updateOpeningBalance(account.id, e.target.value)}
                            placeholder="0.00"
                            className="w-40 text-right"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}

              <div className="flex justify-end mt-6">
                <Button 
                  onClick={handleSaveOpeningBalances}
                  disabled={Math.abs(totals.difference) >= 0.01 || !canEdit}
                  className="bg-[hsl(var(--brand-500))] hover:bg-[hsl(var(--brand-600))] text-white"
                  title={!canEdit ? "You don't have permission to edit opening balances" : ""}
                >
                  Save Opening Balances
                </Button>
              </div>
              {!canEdit && (
                <p className="text-sm text-amber-600 text-right mt-2">You don't have permission to modify opening balances</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Periods Tab */}
        <TabsContent value="periods">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar size={20} />
                Period Management
              </CardTitle>
              <CardDescription>
                Close monthly periods to prevent modifications to transactions. Closed periods cannot have new transactions posted.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {periods.length === 0 ? (
                <div className="text-center py-8 text-[hsl(var(--muted-ink))]">
                  <Calendar className="mx-auto h-12 w-12 mb-4" />
                  <p>No transaction periods yet. Periods will appear as you record transactions.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {periods.map(period => (
                    <div 
                      key={period.period} 
                      className="flex items-center justify-between p-4 border rounded-lg"
                    >
                      <div className="flex items-center gap-4">
                        {period.is_closed ? (
                          <Lock className="text-gray-400" size={20} />
                        ) : (
                          <Unlock className="text-green-500" size={20} />
                        )}
                        <div>
                          <p className="font-medium">{period.period}</p>
                          <p className="text-sm text-[hsl(var(--muted-ink))]">
                            {period.transaction_count} transaction{period.transaction_count !== 1 ? 's' : ''}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {period.is_closed ? (
                          <>
                            <Badge variant="secondary">Closed</Badge>
                            {isAdmin && (
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => handleReopenPeriod(period.period)}
                              >
                                Reopen
                              </Button>
                            )}
                          </>
                        ) : (
                          canEdit && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => { setSelectedPeriod(period); setClosePeriodDialog(true); }}
                            >
                              <Lock size={14} className="mr-1" />
                              Close Period
                            </Button>
                          )
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Close Period Dialog */}
      <Dialog open={closePeriodDialog} onOpenChange={setClosePeriodDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Close Period</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-[hsl(var(--muted-ink))]">
              Are you sure you want to close period <strong>{selectedPeriod?.period}</strong>?
            </p>
            <p className="mt-2 text-sm text-[hsl(var(--muted-ink))]">
              This will prevent any new transactions from being posted to this period. 
              Existing transactions cannot be edited or voided after closing.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setClosePeriodDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleClosePeriod}
              className="bg-[hsl(var(--brand-500))] hover:bg-[hsl(var(--brand-600))] text-white"
            >
              <Lock size={14} className="mr-1" />
              Close Period
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Category Dialog */}
      <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Add Category</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateCategory} className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input
                value={newCategory.name}
                onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                placeholder="e.g., Marketing"
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label>Type</Label>
              <Select
                value={newCategory.category_type}
                onValueChange={(value) => setNewCategory({ ...newCategory, category_type: value })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="revenue">Revenue</SelectItem>
                  <SelectItem value="expense">Expense</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Color</Label>
              <Input
                type="color"
                value={newCategory.color}
                onChange={(e) => setNewCategory({ ...newCategory, color: e.target.value })}
                className="mt-1 h-10 w-20"
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setCategoryDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-[hsl(var(--brand-500))] hover:bg-[hsl(var(--brand-600))] text-white">
                Create Category
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Counterparty Dialog */}
      <Dialog open={counterpartyDialogOpen} onOpenChange={setCounterpartyDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Add Contact</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateCounterparty} className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input
                value={newCounterparty.name}
                onChange={(e) => setNewCounterparty({ ...newCounterparty, name: e.target.value })}
                placeholder="Contact name"
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label>Type</Label>
              <Select
                value={newCounterparty.counterparty_type}
                onValueChange={(value) => setNewCounterparty({ ...newCounterparty, counterparty_type: value })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="customer">Customer</SelectItem>
                  <SelectItem value="supplier">Supplier</SelectItem>
                  <SelectItem value="lender">Lender</SelectItem>
                  <SelectItem value="employee">Employee</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Email (optional)</Label>
              <Input
                type="email"
                value={newCounterparty.email}
                onChange={(e) => setNewCounterparty({ ...newCounterparty, email: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Phone (optional)</Label>
              <Input
                value={newCounterparty.phone}
                onChange={(e) => setNewCounterparty({ ...newCounterparty, phone: e.target.value })}
                className="mt-1"
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setCounterpartyDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-[hsl(var(--brand-500))] hover:bg-[hsl(var(--brand-600))] text-white">
                Create Contact
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Settings;
